<template>
  <div class="feedback-agent" :class="{ 'feedback-agent--compact': isCompact }">
    <header class="feedback-agent__header">
      <div class="feedback-agent__header-inner">
        <div class="feedback-agent__title-block">
          <div class="feedback-agent__title">Rule Agent</div>
          <p v-if="!isCompact" class="feedback-agent__subtitle">
            Configure how this document is extracted — just describe what should change.
          </p>
        </div>
      </div>
    </header>

    <main class="feedback-agent__messages" ref="messageListRef" aria-live="polite">
      <div class="feedback-agent__messages-inner">
        <div v-if="messages.length === 0 && !bootError" class="feedback-agent__empty">
          <div class="feedback-agent__empty-icon">
            <q-icon name="forum" size="20px" />
          </div>
          <h2>How can I help with this document?</h2>
          <p>Ask why a field looks wrong, or propose a rule for this vendor or buyer.</p>

          <ul class="feedback-agent__suggestions">
            <li v-for="suggestion in suggestions" :key="suggestion">
              <button type="button" @click="applySuggestion(suggestion)">{{ suggestion }}</button>
            </li>
          </ul>

          <p class="feedback-agent__disclaimer" role="note">
            <q-icon name="info" size="13px" aria-hidden="true" />
            <span>
              Rule Agent is AI-powered and can make mistakes — review suggestions
              before applying. Configuration changes affect live extractions.
              Don't paste sensitive customer data. Prompts and user ID are logged
              for audit.
            </span>
          </p>
        </div>

        <template v-else>
          <template v-for="message in messages" :key="message.id">
            <div
              v-if="message.role === 'system'"
              class="feedback-agent__system"
              role="note"
            >
              <span>{{ message.text }}</span>
            </div>
            <ChatMessage
              v-else
              :role="message.role"
              :blocks="message.blocks"
              :pending="message.pending"
              @confirm-proposal="handleConfirmProposal($event)"
              @preview-proposal="handlePreviewProposal($event)"
              @preview-bundle="handlePreviewBundle($event)"
              @dismiss-proposal="handleDismissProposal($event)"
            />
          </template>
        </template>

        <p v-if="runError" class="feedback-agent__inline-error">
          <q-icon name="error_outline" size="14px" />
          <span>{{ runError }}</span>
        </p>
      </div>
    </main>

    <footer class="feedback-agent__composer-wrap">
      <button
        v-if="!isPinnedToBottom && messages.length > 0"
        type="button"
        class="feedback-agent__jump-latest"
        aria-label="Jump to latest message"
        @click="scrollToBottom({ force: true, behavior: 'smooth' })"
      >
        <q-icon name="arrow_downward" size="14px" />
        <span>Jump to latest</span>
      </button>
      <div class="feedback-agent__composer-inner">
        <ChatComposer
          ref="composerRef"
          v-model="draft"
          :disabled="!canSend"
          @send="handleSend"
        />
        <p class="feedback-agent__hint">
          Press <kbd>Enter</kbd> to send, <kbd>Shift</kbd> + <kbd>Enter</kbd> for a new line.
        </p>
      </div>
    </footer>

    <div v-if="bootError" class="feedback-agent__boot-error">
      <q-icon name="warning_amber" size="18px" />
      <span>{{ bootError }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { api } from '@/api/client';
import { useParentMessaging } from '@/composables/useParentMessaging';
import ChatMessage from './components/ChatMessage.vue';
import ChatComposer from './components/ChatComposer.vue';
import { LIVE_EXTRACTION_KEY } from './injection';
import type {
  ChatBlock,
  ChatEvent,
  ChatEventsResponse,
  DocumentExtraction,
  PreviewBlock,
  ProposalBlock,
  TextBlock,
  ThinkingBlock
} from './types';

interface ChatMessageItem {
  id: string;
  role: 'user' | 'assistant' | 'system';
  blocks: ChatBlock[];
  pending?: boolean;
  runId?: string;
  messageId?: string;
  /**
   * True once a SystemMessage event has been emitted for this run. The next AssistantMessage
   * chunk opens a fresh bubble below the system line instead of appending into this one, so
   * the visual order matches the wire-level seq order (proposals → system nudge → reply).
   */
  closed?: boolean;
  /** For role: 'system' items, the verbatim text from the SystemMessage event. */
  text?: string;
}

const { emit, waitForToken } = useParentMessaging('feedback-agent');

const chatSessionId = ref<string | null>(null);
const messages = ref<ChatMessageItem[]>([]);
const draft = ref('');
const bootError = ref<string | null>(null);
const runError = ref<string | null>(null);
const liveExtraction = ref<DocumentExtraction | null>(null);

provide(LIVE_EXTRACTION_KEY, liveExtraction);

const messageListRef = ref<HTMLElement | null>(null);
const composerRef = ref<InstanceType<typeof ChatComposer> | null>(null);

const isCompact = ref(false);
let resizeObserver: ResizeObserver | null = null;

// Pin-to-bottom auto-scroll: follow new content while the user is at (or near)
// the bottom, but stop following the moment they scroll up to re-read. A small
// threshold absorbs sub-pixel rounding and the gap that opens while a pending
// bubble grows by a few pixels.
const isPinnedToBottom = ref(true);
const SCROLL_PIN_THRESHOLD_PX = 64;

const suggestions = [
  'Why is the invoice number empty?',
  'Always extract the buyer reference from the top-right corner.',
  'For this vendor, ignore the freight line in the totals.'
];

// Derive run-in-progress from the messages themselves rather than mirroring the
// server's runStatus. The long-poll snapshot can land mid-send with the
// user-message event already committed but runStatus still "Idle" (the run
// hasn't transitioned yet), and using that to clear pending bubbles produced
// the empty-bubble race seen on staging.
const isRunning = computed(() => {
  if (messages.value.some((m) => m.role === 'assistant' && m.pending)) return true;
  // Also lock the composer while a proposal action is in flight. Tests can take
  // minutes; Apply can take tens of seconds. Letting the user fire a second
  // turn underneath either is a recipe for confusing state.
  for (const m of messages.value) {
    for (const b of m.blocks) {
      if (b.kind === 'proposal' && (b.busy || b.previewing)) return true;
    }
  }
  return false;
});

// previewRunId → proposalIds whose `previewing` flag should be flipped back to false
// once the run reports a terminal status. Single-proposal runs have one owner; bundle
// runs carry every proposal that was sent in the request.
const previewRunOwners = new Map<string, string[]>();

// Heartbeat ticker for slow tool calls (propose_rule, propose_rule_edit).
//
// The backend emits a single "Preparing a rule proposal — this can take a few
// seconds." Thinking event when the tool starts, and then nothing else until
// the proposal card arrives ~10-30 s later. Without intermediate progress
// the user stares at a frozen status line wondering whether anything is
// happening. The ticker keeps narrating the wait with scripted phases.
//
// Important: the messages are synthetic — not real model output. They exist
// purely to fill the silent gap and are tagged in the block id so it's clear
// in the timeline that they came from the ticker, not the backend. If real
// streaming reasoning ever starts arriving as Thinking events for the same
// run (today the SK Google connector doesn't surface it; see KauntOCRService
// PR #11684 for the backend revert that left the door open), those real
// events stop the ticker on arrival — the same `case 'Thinking'` branch
// below handles both cases.
const HEARTBEAT_TRIGGERS = [
  'Preparing a rule proposal',
  'Preparing an updated rule'
];

const HEARTBEAT_STEPS = [
  'Studying the document layout…',
  'Identifying the rule shape…',
  'Drafting the rule body…',
  'Validating against the schema…',
  'Cross-checking against existing rules on this vendor…',
  'Looking at the label that anchors the value…',
  'Considering whether this rule needs a code script…',
  'Mapping the intent to a field path in the invoice schema…',
  'Making sure the rule won’t be overwritten by a later stage…',
  'Composing a preview-friendly description…',
  'Sanity-checking against the locked field formats…',
  'Translating domain terms back into anchor language…',
  'Estimating how the rule will behave on similar invoices…',
  'Checking whether this resembles a previously dismissed proposal…',
  'Picking the narrowest scope that covers the intent…',
  'Reading the surrounding text near the target value…',
  'Settling on the trigger condition…',
  'Confirming the field path exists in the canonical schema…',
  'Walking through the invoice lines one by one…',
  'Double-checking the anchor label appears on every invoice page…',
  'Sketching out how the extraction will look after the rule runs…',
  'Weighing the rule against the pipeline’s stage order…',
  'Looking for edge cases the rule should handle gracefully…',
  'Tightening the wording so the rule generalises…'
];

const HEARTBEAT_INTERVAL_MS = 5000;

interface HeartbeatState {
  intervalId: ReturnType<typeof setInterval>;
  step: number;
  // Pre-shuffled view over HEARTBEAT_STEPS — each run cycles through the
  // steps in its own random order so two consecutive rule proposals don't
  // narrate the same phases in lockstep. Shuffled once at startHeartbeat;
  // tickHeartbeat just walks the array (cycling if the wait outlasts the
  // list, which is rare — 24 steps × 5 s = 120 s before the first repeat).
  order: number[];
}

// runId → ticker state. Keyed by run so concurrent runs (rare but possible
// in side-by-side embeds) don't clobber each other's tickers.
const heartbeats = new Map<string, HeartbeatState>();

function isHeartbeatTrigger(text: string | undefined): boolean {
  if (!text) return false;
  return HEARTBEAT_TRIGGERS.some((t) => text.startsWith(t));
}

function startHeartbeat(runId: string | undefined) {
  if (!runId) return;
  // Idempotent — a duplicate trigger event for the same run shouldn't
  // restart the ticker from step 0 (would visibly reset the progress).
  if (heartbeats.has(runId)) return;
  const state: HeartbeatState = {
    step: 0,
    order: shuffledIndices(HEARTBEAT_STEPS.length),
    intervalId: setInterval(() => tickHeartbeat(runId), HEARTBEAT_INTERVAL_MS)
  };
  heartbeats.set(runId, state);
}

// Fisher-Yates over a fresh [0..n) array. Inline so the heartbeat is fully
// self-contained — pulling a lodash-style shuffle in for one place would
// be overkill, and any reuse can call this directly.
function shuffledIndices(n: number): number[] {
  const out = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function stopHeartbeat(runId: string | undefined) {
  if (!runId) return;
  const state = heartbeats.get(runId);
  if (!state) return;
  clearInterval(state.intervalId);
  heartbeats.delete(runId);
}

function stopAllHeartbeats() {
  for (const state of heartbeats.values()) clearInterval(state.intervalId);
  heartbeats.clear();
}

function tickHeartbeat(runId: string) {
  const state = heartbeats.get(runId);
  if (!state) return;

  const target = [...messages.value].reverse().find(
    (m) => m.role === 'assistant' && m.runId === runId && !m.closed
  );
  // If the bubble was closed (SystemMessage / Error / route change) since
  // the timer fired, stop ticking — there's nothing left to append to and
  // the closing event should have called stopHeartbeat already; this is
  // defence in depth for any race we missed.
  if (!target) {
    stopHeartbeat(runId);
    return;
  }

  // Update the existing thinking block's text in place rather than
  // pushing a new one. Keeping the same block (and therefore the same
  // startedAt) means the seconds counter in ChatMessage ticks
  // continuously from the original Thinking event — total wait, not
  // time since the last phrase change. The trigger Thinking event was
  // appended first by the case 'Thinking' branch, so the block already
  // exists at this point; if it somehow doesn't, bail.
  const block = target.blocks.find((b) => b.kind === 'thinking') as
    | ThinkingBlock
    | undefined;
  if (!block) {
    stopHeartbeat(runId);
    return;
  }
  block.text = HEARTBEAT_STEPS[state.order[state.step % state.order.length]];
  state.step++;
}

const canSend = computed(() =>
  !isRunning.value && chatSessionId.value !== null && bootError.value === null);

let activePollAbort: AbortController | null = null;
let pollingLoop: Promise<void> | null = null;
let cursor = 0;

async function bootSession() {
  try {
    await waitForToken();
  } catch {
    bootError.value = 'Could not connect — token timed out.';
    return;
  }

  try {
    const response = await api.ruleAssistant.createSession();
    chatSessionId.value = response.data.sessionId;
    emit('ready');
    // Fire-and-forget the baseline extraction. It only feeds the preview-diff
    // renderer, so we don't block boot if it's slow or fails.
    void loadLiveExtraction();
    // Start a long-poll loop immediately so server-initiated events (e.g. a
    // proposal arriving without a fresh user message) still reach the UI.
    pollingLoop = runPollingLoop();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to start chat session';
    bootError.value = message;
    emit('error', { code: 'CHAT_SESSION_FAILED', message });
  }
}

async function loadLiveExtraction() {
  try {
    const response = await api.ruleAssistant.getDocument();
    liveExtraction.value = response.data as DocumentExtraction;
  } catch {
    // Soft fail — the preview renderer falls back to "—" → new value rows
    // and the rest of the chat keeps working without the baseline.
  }
}

async function handleSend() {
  if (!canSend.value || !chatSessionId.value) return;
  const text = draft.value.trim();
  if (!text) return;

  draft.value = '';
  runError.value = null;

  const userMessage: ChatMessageItem = {
    id: `user-${Date.now()}`,
    role: 'user',
    blocks: [{ kind: 'text', id: 'text', text }]
  };
  messages.value.push(userMessage);
  // Sending is an explicit user action — always jump to the bottom, even if
  // they were scrolled up reading earlier messages.
  scrollToBottom({ force: true });

  const assistantMessage: ChatMessageItem = {
    id: `assistant-${Date.now()}`,
    role: 'assistant',
    blocks: [],
    pending: true
  };
  messages.value.push(assistantMessage);

  try {
    const response = await api.ruleAssistant.sendMessage(chatSessionId.value, text);
    assistantMessage.runId = response.data.runId;
    // The polling loop is already running; it will pick up events for this runId.
  } catch (err) {
    assistantMessage.pending = false;
    const message = err instanceof Error ? err.message : 'Failed to send message';
    runError.value = message;
    emit('error', { code: 'CHAT_SEND_FAILED', message });
  } finally {
    composerRef.value?.focus();
  }
}

async function runPollingLoop() {
  if (!chatSessionId.value) return;

  let consecutiveErrors = 0;
  const MAX_CONSECUTIVE_ERRORS = 8;

  while (chatSessionId.value) {
    activePollAbort = new AbortController();
    let payload: ChatEventsResponse;
    try {
      const response = await api.ruleAssistant.getSessionEvents(
        chatSessionId.value, cursor, 25, activePollAbort.signal
      );
      payload = response.data as ChatEventsResponse;
      consecutiveErrors = 0;
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'ERR_CANCELED') return;

      // 404 means the chat session is gone (replica without state, server-side
      // expiry, etc.). Polling won't recover; stop and surface a real error.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        runError.value = 'Chat session is no longer available. Reload to start a new one.';
        clearPendingAssistantBubbles();
        return;
      }

      consecutiveErrors += 1;
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        runError.value = 'Lost connection to the chat backend.';
        clearPendingAssistantBubbles();
        return;
      }
      // Exponential-ish backoff so transient failures don't pin the server.
      await delay(Math.min(500 * consecutiveErrors, 3000));
      continue;
    } finally {
      activePollAbort = null;
    }

    for (const event of payload.events) {
      applyEvent(event);
    }
    if (payload.events.length > 0) {
      cursor = Math.max(cursor, payload.latestSeq);
    }
    // The deep watcher on `messages` handles auto-scroll for any mutation
    // applyEvent makes, so we don't need to scroll here.
  }
}

function applyEvent(event: ChatEvent) {
  switch (event.kind) {
    case 'UserMessage':
      // The local bubble already covers this; replayed messages from the server
      // are ignored to keep duplicates out of the timeline.
      break;

    case 'Thinking':
      if (event.text) appendThinking(event, event.text);
      // Trigger-text Thinking starts the heartbeat; any other Thinking on the
      // same run (real streaming reasoning, if/when it ever arrives) stops it
      // so the trail doesn't fight with real model output.
      if (isHeartbeatTrigger(event.text)) {
        startHeartbeat(event.runId);
      } else {
        stopHeartbeat(event.runId);
      }
      break;

    case 'AssistantMessage':
      stopHeartbeat(event.runId);
      if (event.text) writeAssistantText(event, event.text);
      break;

    case 'SystemMessage':
      stopHeartbeat(event.runId);
      if (event.text) appendSystemMessage(event, event.text);
      break;

    case 'ProposalCreated':
      stopHeartbeat(event.runId);
      if (event.proposal) attachProposal(event);
      break;

    case 'ProposalDiscarded':
      stopHeartbeat(event.runId);
      if (event.proposal) markProposalDismissed(event.proposal.proposalId);
      break;

    case 'PermissionDenied':
      stopHeartbeat(event.runId);
      if (event.permissionDenied) attachPermissionDenied(event);
      break;

    case 'PreviewRunStarted':
      // Event kind is authoritative; no need to read upstream's status string.
      if (event.previewRunId) attachPreview(event, event.previewRunId, 'Running');
      break;

    case 'PreviewRunFinished':
      // Same — Finished means the run terminated. The GET is only for the
      // result payload (resultInlineJson / errorMessage).
      if (event.previewRunId) void completePreviewRun(event.previewRunId);
      break;

    case 'Error':
      stopHeartbeat(event.runId);
      runError.value = event.text || 'The assistant ran into an error.';
      // Clear pending on the bubble this error belongs to so the composer
      // unlocks instead of waiting forever on a run that won't produce text.
      for (const m of messages.value) {
        if (m.role === 'assistant' && m.pending && (!event.runId || m.runId === event.runId)) {
          m.pending = false;
        }
      }
      break;
  }
}

function targetForRun(event: ChatEvent): ChatMessageItem {
  const runId = event.runId;
  if (runId) {
    // Skip bubbles closed by a SystemMessage so the next AssistantMessage opens a
    // fresh bubble below the system line rather than back-filling above it.
    const match = [...messages.value].reverse().find(
      (m) => m.role === 'assistant' && m.runId === runId && !m.closed
    );
    if (match) return match;
  }
  // Server-initiated events (no matching local bubble) get a fresh assistant
  // bubble so they still surface in the timeline.
  const fresh: ChatMessageItem = {
    id: `assistant-${event.eventId}`,
    role: 'assistant',
    blocks: [],
    pending: false,
    runId: event.runId
  };
  messages.value.push(fresh);
  return fresh;
}

function writeAssistantText(event: ChatEvent, text: string) {
  const target = targetForRun(event);
  target.messageId = event.messageId ?? target.messageId;
  target.pending = false;
  // Thinking blocks were progress hints leading up to this reply; drop them
  // so the final assistant text replaces, not appends to, the trail.
  target.blocks = target.blocks.filter((b) => b.kind !== 'thinking');
  const last = target.blocks[target.blocks.length - 1];
  if (last && last.kind === 'text') {
    (last as TextBlock).text = text;
    return;
  }
  target.blocks.push({
    kind: 'text',
    id: `text-${event.eventId}`,
    text
  });
}

// SystemMessage events arrive right after a ProposalCreated burst and usually right before
// the assistant's final AssistantMessage. We render them as a muted line outside any bubble
// and close the run's current assistant bubble so the final reply opens fresh below.
function appendSystemMessage(event: ChatEvent, text: string) {
  const runId = event.runId;
  if (runId) {
    for (const m of messages.value) {
      if (m.role === 'assistant' && m.runId === runId && !m.closed) {
        m.closed = true;
        // Clear pending so the composer unlocks. SystemMessage marks the point where the
        // run hands control back to the user — either to act on the proposal card or to
        // type a reply. If a final AssistantMessage arrives afterwards it opens a fresh
        // bubble (closed bubbles are skipped by targetForRun), so unlocking now doesn't
        // collide with later streaming.
        m.pending = false;
      }
    }
  }
  messages.value.push({
    id: `system-${event.eventId}`,
    role: 'system',
    blocks: [],
    text,
    runId
  });
}

// One live "I'm thinking" line per run. Backend Thinking events and
// heartbeat ticks both flow through here: if a thinking block already
// exists for this run, we update its text in place rather than stacking
// a second block. startedAt is preserved across updates so the UI's
// seconds counter ticks continuously from the very first Thinking event
// of the run (the seconds reflect the user's total wait, not the time
// since the latest phrase change).
function appendThinking(event: ChatEvent, text: string) {
  const target = targetForRun(event);
  const existing = target.blocks.find((b) => b.kind === 'thinking') as
    | ThinkingBlock
    | undefined;
  if (existing) {
    existing.text = text;
    return;
  }
  target.blocks.push({
    kind: 'thinking',
    id: `thinking-${event.eventId}`,
    text,
    startedAt: Date.now()
  } as ThinkingBlock);
}

function attachProposal(event: ChatEvent) {
  if (!event.proposal) return;
  const target = targetForRun(event);
  target.blocks.push({
    kind: 'proposal',
    id: `proposal-${event.proposal.proposalId}`,
    proposal: event.proposal,
    busy: false,
    previewing: false,
    committed: false,
    dismissed: false,
    dismissing: false
  });
}

// Drives the dismissed visual when a ProposalDiscarded event arrives from the timeline —
// typically from another tab acting on the same session. No-op for unknown ids: the
// matching ProposalCreated must have been seen earlier or in the same poll batch for the
// card to exist, and we don't synthesize muted cards out of thin air.
function markProposalDismissed(proposalId: string) {
  const block = findProposalBlock(proposalId);
  if (!block || block.committed) return;
  block.dismissed = true;
  block.dismissing = false;
  block.error = null;
}

function attachPermissionDenied(event: ChatEvent) {
  const target = targetForRun(event);
  target.blocks.push({
    kind: 'permission-denied',
    id: `denied-${event.eventId}`,
    reason: event.permissionDenied?.reason ?? 'Permission denied.',
    alternative: event.permissionDenied?.alternative ?? undefined
  });
}

function attachPreview(event: ChatEvent, previewRunId: string, status: PreviewBlock['status']) {
  if (hasPreviewBlock(previewRunId)) return;
  const target = targetForRun(event);
  target.blocks.push({
    kind: 'preview',
    id: `preview-${previewRunId}`,
    previewRunId,
    status
  });
}

// Self-attach a preview block as soon as the Test POST returns. PreviewRunStarted
// is not always emitted by upstream; without this the chat shows nothing between
// Test click and PreviewRunFinished, and the diff renderer has no block to write
// into when Finished fires.
function attachPreviewForProposal(
  proposalId: string,
  previewRunId: string,
  summaryOverride?: string
) {
  if (hasPreviewBlock(previewRunId)) return;
  const target = findMessageContainingProposal(proposalId);
  if (!target) return;
  const proposalBlock = findProposalBlock(proposalId);
  target.blocks.push({
    kind: 'preview',
    id: `preview-${previewRunId}`,
    previewRunId,
    status: 'Running',
    proposalSummary: summaryOverride ?? proposalBlock?.proposal.summary
  });
}

function hasPreviewBlock(previewRunId: string): boolean {
  for (const m of messages.value) {
    for (const b of m.blocks) {
      if (b.kind === 'preview' && b.previewRunId === previewRunId) return true;
    }
  }
  return false;
}

function findMessageContainingProposal(proposalId: string): ChatMessageItem | null {
  for (const message of messages.value) {
    for (const block of message.blocks) {
      if (block.kind === 'proposal' && block.proposal.proposalId === proposalId) {
        return message;
      }
    }
  }
  return null;
}

function clearPendingAssistantBubbles() {
  for (const message of messages.value) {
    if (message.role === 'assistant' && message.pending) message.pending = false;
  }
}

// Called only on PreviewRunFinished — the event itself signals termination,
// so we don't need to read upstream's status string. The GET is purely for
// the result payload; errorMessage being set is what flips Failed vs Completed.
async function completePreviewRun(previewRunId: string) {
  if (!chatSessionId.value) return;
  try {
    const response = await api.ruleAssistant.getPreviewRun(chatSessionId.value, previewRunId);
    const result = response.data as { errorMessage?: string; resultInlineJson?: string };
    updatePreviewBlock(previewRunId, {
      status: result.errorMessage ? 'Failed' : 'Completed',
      errorMessage: result.errorMessage,
      resultInlineJson: result.resultInlineJson
    });
  } catch {
    updatePreviewBlock(previewRunId, {
      status: 'Failed',
      errorMessage: 'Could not load preview result.'
    });
  }
}

function updatePreviewBlock(previewRunId: string, patch: Partial<PreviewBlock>) {
  for (const message of messages.value) {
    for (const block of message.blocks) {
      if (block.kind === 'preview' && block.previewRunId === previewRunId) {
        Object.assign(block, patch);
        break;
      }
    }
  }
  if (patch.status === 'Completed' || patch.status === 'Failed') {
    const ownerIds = previewRunOwners.get(previewRunId);
    if (ownerIds) {
      for (const ownerId of ownerIds) {
        const proposal = findProposalBlock(ownerId);
        if (proposal) proposal.previewing = false;
      }
      previewRunOwners.delete(previewRunId);
    }
  }
}

async function handleConfirmProposal(proposalId: string) {
  if (!chatSessionId.value) return;
  const block = findProposalBlock(proposalId);
  if (!block || block.busy || block.committed) return;

  block.busy = true;
  block.error = null;
  try {
    const response = await api.ruleAssistant.confirmProposal(chatSessionId.value, proposalId);
    const result = response.data as {
      status: string;
      committedRuleId?: string;
      error?: string;
    };
    // Upstream reports the confirmed state through either an explicit status
    // (case varies — "Committed" / "committed" / "confirmed" have all shown up)
    // or a populated committedRuleId. Trust either signal as success.
    const status = (result.status ?? '').toLowerCase();
    const succeeded = !!result.committedRuleId
      || status === 'committed' || status === 'confirmed' || status === 'applied';
    if (succeeded) {
      block.committed = true;
      block.committedRuleId = result.committedRuleId ?? null;
    } else {
      block.error = result.error || `Proposal ${status || 'failed'}.`;
    }
  } catch (err) {
    block.error = err instanceof Error ? err.message : 'Could not apply proposal.';
  } finally {
    block.busy = false;
  }
}

async function handleDismissProposal(proposalId: string) {
  if (!chatSessionId.value) return;
  const block = findProposalBlock(proposalId);
  if (!block || block.busy || block.committed || block.dismissed || block.dismissing) return;

  // Optimistic transition — flip the visual immediately and only revert on a non-2xx
  // response. `alreadyDecided` is treated as a soft success: the card is no longer
  // pending upstream, so dismissed is a reasonable rendering even if the actual terminal
  // state was committed/superseded/expired. A follow-up GET could refine that but is
  // overkill for the common case where another tab beat us to it.
  block.dismissing = true;
  block.error = null;
  try {
    const response = await api.ruleAssistant.discardProposal(chatSessionId.value, proposalId);
    const result = response.data as { status?: string; error?: string };
    const status = (result.status ?? '').toLowerCase();
    if (status === 'discarded' || status === 'alreadydecided') {
      block.dismissed = true;
    } else if (status === 'notfound') {
      // Card is stale — remove it. Surfacing an inline error and removing matches the
      // spec's "surface a small inline error and remove the stale card."
      removeProposalBlock(proposalId);
      runError.value = 'That proposal is no longer in this session.';
    } else {
      block.error = result.error || 'Couldn\'t dismiss; try again.';
    }
  } catch (err) {
    block.error = err instanceof Error ? err.message : 'Couldn\'t dismiss; try again.';
  } finally {
    block.dismissing = false;
  }
}

function removeProposalBlock(proposalId: string) {
  for (const message of messages.value) {
    const idx = message.blocks.findIndex(
      (b) => b.kind === 'proposal' && b.proposal.proposalId === proposalId
    );
    if (idx !== -1) {
      message.blocks.splice(idx, 1);
      return;
    }
  }
}

async function handlePreviewBundle(proposalIds: string[]) {
  if (!chatSessionId.value || proposalIds.length === 0) return;
  const blocks = proposalIds
    .map(findProposalBlock)
    .filter((b): b is ProposalBlock => b !== null && !b.previewing && !b.committed);
  if (blocks.length === 0) return;

  for (const block of blocks) block.previewing = true;
  try {
    const response = await api.ruleAssistant.startBundlePreview(
      chatSessionId.value,
      blocks.map((b) => b.proposal.proposalId)
    );
    const data = response.data as
      | { previewRunId?: string; invalidProposalIds?: string[] | null; error?: string | null }
      | undefined;
    const previewRunId = data?.previewRunId;
    const invalid = new Set(data?.invalidProposalIds ?? []);
    if (data?.error || !previewRunId) {
      for (const block of blocks) block.previewing = false;
      runError.value = data?.error ?? 'Could not start bundle preview.';
      return;
    }

    // Clear the previewing flag on any proposals upstream excluded from the run — they
    // won't get a flip from completePreviewRun and would otherwise spin forever.
    const owners: string[] = [];
    for (const block of blocks) {
      if (invalid.has(block.proposal.proposalId)) {
        block.previewing = false;
      } else {
        owners.push(block.proposal.proposalId);
      }
    }
    if (owners.length === 0) {
      runError.value = 'No proposals in this bundle could be previewed.';
      return;
    }

    previewRunOwners.set(previewRunId, owners);
    // Bundle previews aggregate multiple rules into one combined diff, so the headline
    // can't honestly be one rule's summary — that would imply the diff reflects only
    // that rule's effect. Use a chain-aware label instead; the change list below shows
    // the actual combined effect.
    const headline = owners.length > 1
      ? `Combined preview of ${owners.length} rules`
      : undefined;
    attachPreviewForProposal(owners[0], previewRunId, headline);
  } catch (err) {
    for (const block of blocks) block.previewing = false;
    runError.value = err instanceof Error ? err.message : 'Could not start bundle preview.';
  }
}

async function handlePreviewProposal(proposalId: string) {
  if (!chatSessionId.value) return;
  const block = findProposalBlock(proposalId);
  if (!block || block.previewing || block.committed) return;

  block.previewing = true;
  try {
    const response = await api.ruleAssistant.startPreview(chatSessionId.value, proposalId);
    const previewRunId = response.data?.previewRunId as string | undefined;
    if (previewRunId) {
      previewRunOwners.set(previewRunId, [proposalId]);
      // Attach the preview block ourselves so the diff renderer has somewhere
      // to write into when PreviewRunFinished arrives — upstream's
      // PreviewRunStarted event isn't always emitted.
      attachPreviewForProposal(proposalId, previewRunId);
    } else {
      // Upstream didn't return a previewRunId; we have no way to know when the
      // run finishes, so flip back now rather than hanging the button forever.
      block.previewing = false;
    }
    // PreviewRunFinished, when it fires via the long-poll loop, calls
    // completePreviewRun → updatePreviewBlock which flips block.previewing.
  } catch (err) {
    block.previewing = false;
    runError.value = err instanceof Error ? err.message : 'Could not start preview.';
  }
}

function findProposalBlock(proposalId: string): ProposalBlock | null {
  for (const message of messages.value) {
    for (const block of message.blocks) {
      if (block.kind === 'proposal' && block.proposal.proposalId === proposalId) {
        return block as ProposalBlock;
      }
    }
  }
  return null;
}

function scrollToBottom(opts: { force?: boolean; behavior?: ScrollBehavior } = {}) {
  const { force = false, behavior = 'auto' } = opts;
  if (!force && !isPinnedToBottom.value) return;
  nextTick(() => {
    const list = messageListRef.value;
    if (!list) return;
    list.scrollTo({ top: list.scrollHeight, behavior });
    // Eagerly mark pinned so the jump-to-latest button doesn't flicker
    // between the programmatic scroll and the resulting scroll event.
    isPinnedToBottom.value = true;
  });
}

function handleMessagesScroll() {
  const list = messageListRef.value;
  if (!list) return;
  const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
  isPinnedToBottom.value = distance <= SCROLL_PIN_THRESHOLD_PX;
}

// Deep-watch the timeline so streaming text chunks (which mutate an existing
// block's `text` rather than pushing a new message) also keep the view pinned.
// `flush: 'post'` waits for the DOM update so scrollHeight reflects the new
// content before we scroll.
watch(messages, () => scrollToBottom(), { deep: true, flush: 'post' });

function applySuggestion(text: string) {
  draft.value = text;
  composerRef.value?.focus();
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

onMounted(() => {
  bootSession();

  // Empty-state opens straight into the composer so users can start typing
  // without a click. nextTick lets the textarea mount before we focus it.
  nextTick(() => composerRef.value?.focus());

  messageListRef.value?.addEventListener('scroll', handleMessagesScroll, { passive: true });

  // Bound the page to the viewport while this view is mounted. The default
  // .kaunt-page uses min-height: 100vh so other routes can grow with content,
  // but chat needs a fixed parent height for the messages region's
  // flex: 1; overflow: auto to actually overflow. Without this the iframe
  // document scrolls instead of the messages list, and auto-scroll silently
  // no-ops.
  document.querySelector('.kaunt-page')?.classList.add('kaunt-page--bounded');

  // Compact mode kicks in below ~420 px so headers and helper copy collapse on
  // narrow embeds without forcing a separate mobile breakpoint.
  const root = document.querySelector('.feedback-agent') as HTMLElement | null;
  if (root && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? window.innerWidth;
      isCompact.value = width < 420;
    });
    resizeObserver.observe(root);
  }
});

onBeforeUnmount(() => {
  messageListRef.value?.removeEventListener('scroll', handleMessagesScroll);
  resizeObserver?.disconnect();
  document.querySelector('.kaunt-page')?.classList.remove('kaunt-page--bounded');
  chatSessionId.value = null;
  activePollAbort?.abort();
  void pollingLoop;
  // Clear any in-flight tickers so they don't keep firing after the view
  // unmounts. Each interval holds a closure over `messages`, so leaks here
  // would silently mutate ref state from a destroyed component.
  stopAllHeartbeats();
});
</script>

<style scoped lang="scss">
.feedback-agent {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  // Fill the parent (router-view / q-page). The view toggles the
  // .kaunt-page--bounded class on mount so the q-page resolves to 100vh
  // instead of growing with content; height: 100% here then gives the
  // messages region a real height to overflow inside.
  height: 100%;
  min-height: 0;
  position: relative;
  background: #fafbfc;
  color: #111827;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  // Establish a size container so all descendant components can adapt to the
  // chat's own width (300 px sidebar embed through full-page) instead of the
  // viewport. Avoid `container-type: size` — it would also constrain height
  // and break the flex column that gives the messages region its scroll area.
  container-type: inline-size;
  container-name: ra;

  &__header {
    flex-shrink: 0;
    padding: 6px 16px;
    background: #ffffff;
    border-bottom: 1px solid #eef0f3;
  }

  &__header-inner,
  &__messages-inner,
  &__composer-inner {
    width: 100%;
    margin: 0 auto;
  }

  &__title-block { display: flex; flex-direction: column; gap: 2px; }

  &__title {
    margin: 0;
    padding: 0;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: #111827;
  }

  &__subtitle {
    margin: 0;
    padding: 0;
    font-size: 12px;
    line-height: 1.4;
    color: #6b7280;
  }

  &__messages {
    flex: 1;
    overflow-y: auto;
    padding: 18px 16px 8px;
    scroll-behavior: smooth;
    scrollbar-gutter: stable;
  }

  &__empty {
    text-align: center;
    color: #4b5563;
    padding: 24px 12px 8px;
    max-width: 420px;
    margin: 0 auto;

    h2 {
      font-size: 15px;
      font-weight: 600;
      margin: 12px 0 4px;
      color: #111827;
    }

    p { font-size: 13px; margin: 0; color: #6b7280; }
  }

  &__empty-icon {
    width: 40px;
    height: 40px;
    margin: 0 auto;
    border-radius: 50%;
    background: linear-gradient(135deg, #eef2ff, #e0eafc);
    color: #3b6cf3;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__suggestions {
    list-style: none;
    padding: 0;
    margin: 16px 0 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    button {
      width: 100%;
      text-align: left;
      padding: 8px 12px;
      font-size: 13px;
      color: #1f2937;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      cursor: pointer;
      transition: border-color 120ms ease, background-color 120ms ease;

      &:hover { border-color: #93b4f5; background: #f5f8ff; }
    }
  }

  // Shown only inside the empty/welcome state, where there's room. Keeping the
  // warning off the running chat preserves vertical space for messages and the
  // composer at narrow widths.
  &__disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 20px 0 0;
    padding-top: 12px;
    border-top: 1px solid #eef0f3;
    font-size: 11.5px;
    line-height: 1.45;
    color: #9ca3af;
    text-align: left;

    .q-icon {
      flex-shrink: 0;
      margin-top: 1px;
      color: #b0b6bf;
    }
  }

  &__system {
    display: flex;
    justify-content: center;
    margin: 4px 0 12px;
    padding: 0 16px;
    font-size: 12.5px;
    color: #6b7280;
    line-height: 1.4;
    font-style: italic;
    text-align: center;

    span {
      max-width: min(85%, 560px);
      padding: 4px 12px;
      border-radius: 999px;
      background: rgba(229, 231, 235, 0.55);
    }
  }

  &__inline-error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 8px 4px 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 12px;
  }

  &__composer-wrap {
    flex-shrink: 0;
    padding: 10px 14px 14px;
    background: #fafbfc;
    border-top: 1px solid #eef0f3;
    // Pin to the bottom of the chat region. With the flex column above, the
    // composer is already last-in-row; sticky keeps it visible when the host
    // page scrolls the chat element itself rather than the messages list.
    position: sticky;
    bottom: 0;
    z-index: 1;
  }

  &__jump-latest {
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translate(-50%, -100%);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #ffffff;
    color: #1f2937;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(17, 24, 39, 0.12);
    transition: transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease;
    z-index: 2;

    &:hover {
      background: #f5f8ff;
      box-shadow: 0 8px 22px rgba(17, 24, 39, 0.16);
    }

    &:active {
      transform: translate(-50%, calc(-100% + 1px));
    }
  }

  &__hint {
    margin: 6px 4px 0;
    font-size: 11px;
    color: #9ca3af;
    text-align: right;

    kbd {
      display: inline-block;
      padding: 0 4px;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      background: #ffffff;
      font-family: inherit;
      font-size: 10px;
      line-height: 1.4;
      color: #6b7280;
    }
  }

  &__boot-error {
    position: absolute;
    inset: 12px 12px auto 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: #fff7ed;
    color: #9a3412;
    border: 1px solid #fed7aa;
    border-radius: 10px;
    font-size: 13px;
  }

  &--compact {
    .feedback-agent__header { padding: 6px 14px; }
    .feedback-agent__title { font-size: 14px; }
    .feedback-agent__messages { padding: 12px 12px 6px; }
    .feedback-agent__composer-wrap { padding: 8px 10px 12px; }
    .feedback-agent__hint { display: none; }
  }
}

// Container queries: adapt to the chat's own width across the full range from
// a 300 px sidebar to a full-page embed. Combine with the `--compact` JS flag
// rather than replacing it — that flag still drives v-if content (subtitle).

// Ultra-narrow: 300 px sidebars. Trim every horizontal margin and shrink the
// header so the message column gets real estate.
@container ra (max-width: 359px) {
  .feedback-agent__header { padding: 6px 10px; }
  .feedback-agent__title { font-size: 13px; }
  .feedback-agent__messages { padding: 10px 8px 4px; }
  .feedback-agent__composer-wrap { padding: 6px 8px 10px; }
  .feedback-agent__empty { padding: 16px 6px 4px; }
  .feedback-agent__inline-error { margin: 6px 2px 0; font-size: 11.5px; }
  .feedback-agent__boot-error { inset: 8px 8px auto 8px; font-size: 12px; padding: 8px 10px; }
}

// Wider embeds and full-page: cap the readable column and center it so messages
// don't stretch into uncomfortable 1500 px lines.
@container ra (min-width: 720px) {
  .feedback-agent__header { padding: 8px 24px; }
  .feedback-agent__messages { padding: 24px 24px 12px; }
  .feedback-agent__composer-wrap { padding: 14px 24px 18px; }
  .feedback-agent__title { font-size: 16px; }
  .feedback-agent__subtitle { font-size: 13px; }
  .feedback-agent__header-inner,
  .feedback-agent__messages-inner,
  .feedback-agent__composer-inner {
    max-width: 820px;
  }
}

@container ra (min-width: 1080px) {
  .feedback-agent__header { padding: 10px 32px; }
  .feedback-agent__messages { padding: 28px 32px 16px; }
  .feedback-agent__composer-wrap { padding: 16px 32px 22px; }
}
</style>
